import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Check, MagnifyingGlassPlus, MagnifyingGlassMinus, PaperPlaneTilt, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SignaturePad from '../SignaturePad';
import PdfPages, { FIELD_LABELS, useContainerWidth } from './PdfPages';

const isEmpty = (v) => v === undefined || v === null || v === '' || v === false;

const PdfFormFiller = ({ form, file, onSubmit, onBack, submitting }) => {
  const [values, setValues] = useState({});
  const [signing, setSigning] = useState(null);
  const [sigDraft, setSigDraft] = useState('');
  const [zoom, setZoom] = useState(1);
  const [touched, setTouched] = useState(false);
  const [containerRef, containerWidth] = useContainerWidth();
  const pageWidth = Math.max(300, Math.min(containerWidth - 16, 900) * zoom);

  const setVal = (id, v) => setValues(prev => ({ ...prev, [id]: v }));
  const missing = useMemo(() => (form.fields || []).filter(f => f.required && isEmpty(values[f.id])), [form.fields, values]);
  const filled = (form.fields || []).filter(f => !isEmpty(values[f.id])).length;

  const handleSubmit = () => {
    setTouched(true);
    if (missing.length) { toast.error(`Please complete ${missing.length} required field${missing.length !== 1 ? 's' : ''}`); return; }
    onSubmit(values);
  };

  const renderOverlay = (pageIdx, w, h) => (form.fields || []).filter(f => f.page === pageIdx).map(f => {
    const style = { left: f.x * w, top: f.y * h, width: f.w * w, height: f.h * h };
    const bad = touched && f.required && isEmpty(values[f.id]);
    const ring = bad ? 'ring-2 ring-red-500' : 'ring-1 ring-sky-400';
    const fontPx = Math.max(9, Math.min((f.font_size || 11) * (w / (form.pages[pageIdx]?.width || 612)), f.h * h * 0.72));
    if (f.type === 'checkbox') {
      return (
        <button key={f.id} type="button" style={style} title={f.label} onClick={() => setVal(f.id, !values[f.id])}
          className={`absolute rounded-sm bg-sky-100/70 hover:bg-sky-200/80 ${ring} flex items-center justify-center`} data-testid={`fill-${f.id}`}>
          {values[f.id] && <Check weight="bold" className="w-[80%] h-[80%] text-slate-900" />}
        </button>
      );
    }
    if (f.type === 'signature' || f.type === 'initials') {
      const v = values[f.id];
      return (
        <button key={f.id} type="button" style={style} onClick={() => { setSigDraft(v || ''); setSigning(f); }}
          className={`absolute rounded-sm ${v ? 'bg-white' : 'bg-rose-100/70 hover:bg-rose-200/80'} ${ring} overflow-hidden flex items-center justify-center`} data-testid={`fill-${f.id}`}>
          {v ? <img src={v} alt="signature" className="max-w-full max-h-full object-contain" />
             : <span className="text-[10px] sm:text-xs font-medium text-rose-700 truncate px-1">{f.type === 'initials' ? 'Tap to initial' : 'Tap to sign'}{f.required ? ' *' : ''}</span>}
        </button>
      );
    }
    return (
      <input key={f.id} type={f.type === 'date' ? 'date' : 'text'} style={{ ...style, fontSize: fontPx }} value={values[f.id] || ''} placeholder={f.label ? `${f.label}${f.required ? ' *' : ''}` : ''}
        onChange={(e) => setVal(f.id, e.target.value)} title={f.label}
        className={`absolute rounded-sm bg-sky-100/70 focus:bg-white ${ring} px-1 text-slate-900 outline-none focus:ring-2 focus:ring-primary placeholder:text-slate-500/70 leading-none`} data-testid={`fill-${f.id}`} />
    );
  });

  return (
    <div className="flex flex-col h-full" data-testid="pdf-form-filler">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-background sticky top-0 z-20">
        <Button variant="ghost" size="sm" className="rounded-xl gap-1" onClick={onBack} data-testid="filler-back-btn"><ArrowLeft className="w-4 h-4" /> Forms</Button>
        <div className="text-xs text-muted-foreground tabular-nums">{filled}/{form.fields?.length || 0} filled</div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom(z => Math.max(0.75, z - 0.25))}><MagnifyingGlassMinus className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom(z => Math.min(3, z + 0.25))}><MagnifyingGlassPlus className="w-4 h-4" /></Button>
        </div>
      </div>
      {form.description && <p className="text-sm text-muted-foreground px-4 py-2 bg-amber-50 border-b border-amber-100">{form.description}</p>}
      <div ref={containerRef} className="flex-1 overflow-auto bg-slate-200/70 p-2 pb-24">
        <PdfPages file={file} pages={form.pages} pageWidth={pageWidth} renderOverlay={renderOverlay} />
      </div>
      <div className="fixed bottom-0 inset-x-0 bg-background/95 backdrop-blur border-t border-border p-3 flex items-center justify-between gap-3 z-30">
        <p className="text-xs text-muted-foreground">{missing.length ? `${missing.length} required field${missing.length !== 1 ? 's' : ''} left` : 'All required fields complete'}</p>
        <Button className="rounded-xl gap-2" onClick={handleSubmit} disabled={submitting} data-testid="filler-submit-btn">
          <PaperPlaneTilt weight="bold" className="w-4 h-4" /> {submitting ? 'Submitting…' : 'Sign & Submit'}
        </Button>
      </div>

      <Dialog open={!!signing} onOpenChange={(o) => { if (!o) setSigning(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{signing?.label || FIELD_LABELS[signing?.type] || 'Sign'}</DialogTitle>
            <DialogDescription>By signing, you agree this electronic signature is legally binding.</DialogDescription>
          </DialogHeader>
          {signing && <SignaturePad key={signing.id} label={signing.type === 'initials' ? 'Your initials' : 'Your signature'} value={sigDraft} onChange={setSigDraft} testId="onboarding-signature-pad" />}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setSigning(null)}>Cancel</Button>
            <Button className="flex-1 rounded-xl" disabled={!sigDraft} onClick={() => { setVal(signing.id, sigDraft); setSigning(null); }} data-testid="signature-apply-btn">Apply</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PdfFormFiller;
